import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { scanGateTurnstile, checkInAttendee, Attendee } from '../lib/api';
import {
  refreshOfflineScannerManifest,
  validateOfflineGateScan,
  flushOfflineScannerQueues,
  getQueuedGateScans,
} from '../lib/offlineScannerCache';
import {
  ScanLine,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Camera,
  RefreshCw,
  Building2,
  Clock,
  ShieldCheck,
  ShieldAlert,
  Volume2,
  ArrowRight,
  Search,
  UserCheck,
  Info,
  Wifi,
  WifiOff,
  Database,
} from 'lucide-react';
import confetti from 'canvas-confetti';

interface ScanHistoryItem {
  id: string;
  token: string;
  status: 'granted' | 'duplicate' | 'payment_pending' | 'invalid';
  attendeeName?: string;
  companyName?: string | null;
  tier?: string;
  timestamp: string;
  originalCheckedInAt?: string;
  isReplaced?: boolean;
}

export const CheckInScanner: React.FC<{ adminKey?: string }> = ({ adminKey }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [manualToken, setManualToken] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  const [simulateOffline, setSimulateOffline] = useState(false);
  const [cachedBadgeCount, setCachedBadgeCount] = useState<number>(0);
  const [queuedScanCount, setQueuedScanCount] = useState<number>(0);
  const [isSyncingCache, setIsSyncingCache] = useState(false);

  const syncOfflineState = useCallback(async () => {
    setIsSyncingCache(true);
    try {
      if (typeof navigator === 'undefined' || navigator.onLine) {
        await flushOfflineScannerQueues(adminKey);
      }
      const manifest = await refreshOfflineScannerManifest(adminKey);
      if (manifest) {
        setCachedBadgeCount(manifest.entries?.length || 0);
      }
      const queued = await getQueuedGateScans();
      setQueuedScanCount(queued.length);
    } finally {
      setIsSyncingCache(false);
    }
  }, [adminKey]);

  useEffect(() => {
    syncOfflineState();
    const handleOnline = () => {
      setIsOffline(false);
      syncOfflineState();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncOfflineState]);

  // Scan Outcome Display
  const [scanResult, setScanResult] = useState<{
    status: 'granted' | 'duplicate' | 'payment_pending' | 'invalid';
    message: string;
    attendee?: Attendee & { primaryRole?: string };
    originalCheckedInAt?: string;
    token: string;
    isReplaced?: boolean;
    isDuplicate?: boolean;
    roleTheme?: {
      role: string;
      label: string;
      colorHex: string;
      badgeClass: string;
      textClass: string;
      borderClass: string;
    };
  } | null>(null);

  // Recent Desk Scans Log
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const isCooldownRef = useRef(false);
  const autoResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Audio Feedback Synthesizer using Web Audio API
  const playAudioTone = (type: 'granted' | 'duplicate' | 'invalid') => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === 'granted') {
        // High dual chime
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28);
        osc.start();
        osc.stop(ctx.currentTime + 0.28);
      } else if (type === 'duplicate') {
        // Warning dual buzz
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(320, ctx.currentTime);
        osc.frequency.setValueAtTime(260, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc.start();
        osc.stop(ctx.currentTime + 0.35);
      } else {
        // Low error buzz
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(160, ctx.currentTime);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
        osc.start();
        osc.stop(ctx.currentTime + 0.32);
      }
    } catch {
      // Audio autoplay policy fallback
    }
  };

  const processScan = async (tokenRaw: string) => {
    const rawTrimmed = tokenRaw.trim();
    const token = rawTrimmed.includes('/b/')
      ? (rawTrimmed.split('/b/').pop() || rawTrimmed).split('?')[0].split('#')[0].trim()
      : rawTrimmed;
    if (!token || isCooldownRef.current) return;

    isCooldownRef.current = true;
    setIsProcessing(true);

    const effectiveOffline = isOffline || simulateOffline;

    try {
      let response: any;
      if (effectiveOffline) {
        response = await validateOfflineGateScan({
          code: token,
          location: 'Turnstile 1',
          scanType: 'door_entry',
          eventDay: 'day1',
        });
        const queued = await getQueuedGateScans();
        setQueuedScanCount(queued.length);
        if ((response as any).status === 'payment_pending') {
          const pendingErr: any = new Error(response.message);
          pendingErr.status = 403;
          pendingErr.data = { code: 'payment_pending', attendee: response.attendee };
          throw pendingErr;
        }
        if (!response.granted) {
          throw new Error(response.message || 'Pass not recognized in offline cache');
        }
      } else {
        try {
          response = await scanGateTurnstile(
            { code: token, location: 'Turnstile 1', scanType: 'gate_entry' },
            adminKey
          );
        } catch (err: any) {
          if (err.status === 404 || err.status === 401 || err.status === 403 || err.status === 409) {
            throw err;
          }
          // Network failure / Wi-Fi drop fallback: attempt IndexedDB offline validation first
          const offlineFallback = await validateOfflineGateScan({
            code: token,
            location: 'Turnstile 1',
            scanType: 'door_entry',
            eventDay: 'day1',
          });
          if (offlineFallback.granted) {
            response = offlineFallback;
            const queued = await getQueuedGateScans();
            setQueuedScanCount(queued.length);
          } else {
            // Fallback to checkInAttendee for backwards compatibility
            response = await checkInAttendee(token, adminKey);
            response.granted = true;
          }
        }
      }

      if (response.granted) {
        playAudioTone('granted');
        if (!response.isDuplicate && !response.isReplaced) {
          try {
            confetti({
              particleCount: 40,
              spread: 50,
              origin: { y: 0.7 },
              colors: ['#10b981', '#34d399'],
            });
          } catch {
            // fallback
          }
        }

        const msg = response.offlineValidated
          ? response.message
          : response.isReplaced
          ? 'ACCESS GRANTED • Old/replaced badge used. Replaced at desk; ingress permitted.'
          : response.isDuplicate
          ? 'RE-ENTRY GRANTED • Pass verified (Repeat ingress today)'
          : 'Check-in Successful • Turnstile Unlocked';

        const result = {
          status: 'granted' as const,
          message: msg,
          attendee: response.attendee,
          token,
          isReplaced: !!response.isReplaced,
          isDuplicate: !!response.isDuplicate,
          roleTheme: response.theme,
        };

        setScanResult(result);
        setScanHistory((prev) => [
          {
            id: Math.random().toString(36).substring(7),
            token,
            status: 'granted',
            attendeeName: response.attendee?.fullName,
            companyName: response.attendee?.companyName,
            tier: response.attendee?.primaryRole || response.attendee?.tier,
            timestamp: new Date().toLocaleTimeString(),
            isReplaced: !!response.isReplaced,
          },
          ...prev.slice(0, 9),
        ]);
      } else {
        throw new Error(response.message || 'Pass not granted');
      }
    } catch (err: any) {
      if (err.status === 409) {
        playAudioTone('duplicate');
        const duplicateData = err.data;
        const result = {
          status: 'duplicate' as const,
          message: 'ALREADY CHECKED IN • DUPLICATE SCAN DETECTED',
          attendee: duplicateData?.attendee,
          originalCheckedInAt: duplicateData?.checkedInAt,
          token,
        };

        setScanResult(result);
        setScanHistory((prev) => [
          {
            id: Math.random().toString(36).substring(7),
            token,
            status: 'duplicate',
            attendeeName: duplicateData?.attendee?.fullName || 'Registered Attendee',
            companyName: duplicateData?.attendee?.companyName,
            tier: duplicateData?.attendee?.tier,
            timestamp: new Date().toLocaleTimeString(),
            originalCheckedInAt: duplicateData?.checkedInAt,
          },
          ...prev.slice(0, 9),
        ]);
      } else if (
        err.status === 403 ||
        err.data?.code === 'payment_pending' ||
        /payment/i.test(err.message || '')
      ) {
        playAudioTone('duplicate');
        const pendingData = err.data;
        const result = {
          status: 'payment_pending' as const,
          message:
            err.message ||
            'PAYMENT PENDING — Direct Attendee to Registration Helpdesk',
          attendee: pendingData?.attendee,
          token,
        };

        setScanResult(result);
        setScanHistory((prev) => [
          {
            id: Math.random().toString(36).substring(7),
            token,
            status: 'payment_pending',
            attendeeName: pendingData?.attendee?.fullName || 'Unpaid Attendee',
            companyName: pendingData?.attendee?.companyName || 'Payment Pending',
            tier: pendingData?.attendee?.primaryRole || pendingData?.attendee?.tier,
            timestamp: new Date().toLocaleTimeString(),
          },
          ...prev.slice(0, 9),
        ]);
      } else {
        playAudioTone('invalid');
        const result = {
          status: 'invalid' as const,
          message: err.message || 'Invalid Ticket / Unregistered QR Code',
          token,
        };

        setScanResult(result);
        setScanHistory((prev) => [
          {
            id: Math.random().toString(36).substring(7),
            token,
            status: 'invalid',
            attendeeName: 'Unknown Visitor',
            timestamp: new Date().toLocaleTimeString(),
          },
          ...prev.slice(0, 9),
        ]);
      }
    } finally {
      setIsProcessing(false);

      // Auto-reset alert after 3.5 seconds to keep gate turnstile moving
      if (autoResetTimerRef.current) clearTimeout(autoResetTimerRef.current);
      autoResetTimerRef.current = setTimeout(() => {
        setScanResult(null);
        isCooldownRef.current = false;
      }, 3500);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode('qr-reader-viewport');
      }

      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 240, height: 240 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          processScan(decodedText);
        },
        () => {
          // Ignore frequent video frame non-detections
        }
      );

      setIsScanning(true);
    } catch (err: any) {
      console.warn('Camera initiation failed:', err);
      setCameraError(
        err.message ||
          'Camera access restricted or unavailable. Please use the manual token scanner below.'
      );
      setIsScanning(false);
    }
  };

  const stopCamera = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn('Camera stop error:', err);
      }
      setIsScanning(false);
    }
  };

  useEffect(() => {
    return () => {
      if (autoResetTimerRef.current) clearTimeout(autoResetTimerRef.current);
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .catch(() => {})
          .finally(() => {
            html5QrCodeRef.current = null;
          });
      }
    };
  }, []);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualToken.trim()) return;
    processScan(manualToken.trim());
    setManualToken('');
  };

  const getTierBadge = (tier?: string) => {
    switch (tier?.toLowerCase()) {
      case 'vip':
        return 'border-[#E6C687] bg-[#FAF5EA] text-[#785613]';
      case 'conference_delegate':
        return 'border-blue-200 bg-blue-50 text-blue-700';
      case 'exhibitor':
        return 'border-purple-200 bg-purple-50 text-purple-700';
      case 'trade_visitor':
      default:
        return 'border-emerald-200 bg-emerald-50 text-[#0A3D27]';
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 animate-in fade-in duration-200">
      {/* Top Desk Header Banner */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xs">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#0A3D27] to-[#062B1B] flex items-center justify-center text-white font-black shadow-md shadow-[#0A3D27]/20">
            <ScanLine className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Onsite Check-in Desk</h2>
              <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] border border-[#0A3D27]/25">
                Gate Turnstile 1
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              JWCC Pavilion 3 Access Control • Live Turnstile Gate Validation
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <div
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border ${
              isOffline || simulateOffline
                ? 'bg-amber-50 border-amber-300 text-amber-900'
                : 'bg-emerald-50 border-emerald-200 text-[#0A3D27]'
            }`}
          >
            {isOffline || simulateOffline ? (
              <>
                <WifiOff className="w-3.5 h-3.5 text-amber-600" />
                <span>
                  Offline Mode ({queuedScanCount} Queued • {cachedBadgeCount} Cached)
                </span>
              </>
            ) : (
              <>
                <Database className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>
                  Offline Cache Ready ({cachedBadgeCount} Badges
                  {queuedScanCount > 0 ? ` • ${queuedScanCount} Pending Sync` : ''})
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              const next = !simulateOffline;
              setSimulateOffline(next);
              if (!next) {
                syncOfflineState();
              }
            }}
            className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
              simulateOffline
                ? 'bg-amber-600 text-white border-amber-700'
                : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
            }`}
          >
            {simulateOffline ? 'Reconnect Online & Auto-Sync' : 'Test Offline Mode'}
          </button>

          <button
            type="button"
            onClick={syncOfflineState}
            disabled={isSyncingCache}
            title="Refresh IndexedDB Offline Badge Manifest"
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 transition-colors cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncingCache ? 'animate-spin text-[#0A3D27]' : ''}`} />
          </button>

          {!isScanning ? (
            <button
              onClick={startCamera}
              className="flex items-center gap-2 px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <Camera className="w-4 h-4" />
              <span>Activate Camera Scanner</span>
            </button>
          ) : (
            <button
              onClick={stopCamera}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs"
            >
              <span>Stop Camera</span>
            </button>
          )}

          <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-medium text-slate-700">
            <Volume2 className="w-3.5 h-3.5 text-[#0A3D27]" />
            <span>Chimes Active</span>
          </div>
        </div>
      </div>

      {/* Main Scanner Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Live Scanner & Manual Input */}
        <div className="lg:col-span-7 space-y-6">
          {/* Camera Viewport Container */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-xs relative overflow-hidden flex flex-col items-center">
            <div className="w-full flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>Live Optical Viewport</span>
              </span>
              {isScanning && (
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-[#0A3D27] font-bold">
                  <span className="w-2 h-2 rounded-full bg-[#0A3D27] animate-ping" />
                  SCANNING
                </span>
              )}
            </div>

            {/* Video Feed Target */}
            <div className="relative w-full max-w-sm aspect-square bg-slate-100 border border-slate-200 rounded-2xl overflow-hidden flex items-center justify-center">
              <div id="qr-reader-viewport" className="w-full h-full" />

              {!isScanning && (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-white/95 space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500">
                    <Camera className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Camera Offline</p>
                    <p className="text-xs text-slate-500 mt-1 max-w-xs">
                      {cameraError || 'Activate the camera above or use manual barcode entry below.'}
                    </p>
                  </div>
                  <button
                    onClick={startCamera}
                    className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold cursor-pointer transition-colors shadow-xs"
                  >
                    Start Camera
                  </button>
                </div>
              )}

              {/* Scanning Crosshair Overlay */}
              {isScanning && (
                <div className="absolute inset-0 pointer-events-none border-2 border-[#0A3D27]/40 m-8 rounded-2xl flex flex-col justify-between p-2">
                  <div className="flex justify-between">
                    <div className="w-4 h-4 border-t-2 border-l-2 border-[#0A3D27]" />
                    <div className="w-4 h-4 border-t-2 border-r-2 border-[#0A3D27]" />
                  </div>
                  <div className="flex justify-between">
                    <div className="w-4 h-4 border-b-2 border-l-2 border-[#0A3D27]" />
                    <div className="w-4 h-4 border-b-2 border-r-2 border-[#0A3D27]" />
                  </div>
                </div>
              )}
            </div>

            {/* Manual Fallback Barcode / Token Input */}
            <div className="w-full mt-5 pt-4 border-t border-slate-200">
              <form onSubmit={handleManualSubmit} className="space-y-2">
                <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Search className="w-3.5 h-3.5 text-[#0A3D27]" />
                    <span>Manual Token Entry (Barcode / Keyboard)</span>
                  </span>
                  <span className="text-[10px] text-slate-500">Low-light / damaged badge fallback</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                    placeholder="e.g. IIAB26-iMez6rQhpSaS"
                    className="flex-1 bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-mono placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27] transition-all"
                  />
                  <button
                    type="submit"
                    disabled={isProcessing || !manualToken.trim()}
                    className="px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <span>Validate</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Column: Scan Verdict Display & Recent Scan Stream */}
        <div className="lg:col-span-5 space-y-6">
          {/* Active Scan Outcome Card */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-xs relative overflow-hidden min-h-[220px] flex flex-col justify-center">
            {isProcessing ? (
              <div className="text-center py-6 space-y-3">
                <RefreshCw className="w-8 h-8 text-[#0A3D27] animate-spin mx-auto" />
                <p className="text-xs font-bold text-slate-800">Validating badge with database...</p>
              </div>
            ) : scanResult ? (
              <div className="space-y-4 animate-in zoom-in-95 duration-150">
                {scanResult.status === 'granted' && (
                  <div className={`border rounded-2xl p-5 text-center space-y-2.5 shadow-xs ${
                    scanResult.isReplaced
                      ? 'bg-amber-50/90 border-amber-300'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto border ${
                      scanResult.isReplaced
                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : 'bg-emerald-100 text-[#0A3D27] border-emerald-300'
                    }`}>
                      <ShieldCheck className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                      <span className={`text-[10px] font-black uppercase tracking-wider bg-white px-2.5 py-0.5 rounded-full border shadow-xs ${
                        scanResult.isReplaced
                          ? 'text-amber-800 border-amber-300'
                          : 'text-[#0A3D27] border-emerald-200'
                      }`}>
                        {scanResult.isReplaced
                          ? 'Turnstile Open • Replaced Badge'
                          : scanResult.isDuplicate
                          ? 'Turnstile Open • Re-Entry Pass'
                          : 'Access Granted • Turnstile Open'}
                      </span>
                      <h3 className="text-xl font-black text-slate-900">{scanResult.attendee?.fullName}</h3>
                      <p className="text-xs font-medium text-slate-600">
                        {scanResult.attendee?.companyName || 'Trade Visitor / Delegate'}
                      </p>
                      {scanResult.isReplaced && (
                        <p className="text-[11px] font-semibold text-amber-800 bg-amber-100/70 py-1 px-2 rounded-lg mt-1 border border-amber-200">
                          Non-Blocking Scan: Credential replaced at Desk. Ingress allowed.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-2 pt-1">
                      <span
                        className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                          scanResult.roleTheme?.badgeClass || getTierBadge(scanResult.attendee?.primaryRole || scanResult.attendee?.tier)
                        }`}
                      >
                        {scanResult.roleTheme?.label || (scanResult.attendee?.primaryRole || scanResult.attendee?.tier || '').replace('_', ' ')}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">{scanResult.token}</span>
                    </div>
                  </div>
                )}

                {scanResult.status === 'duplicate' && (
                  <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 text-center space-y-2.5 shadow-xs">
                    <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-2xl flex items-center justify-center mx-auto border border-amber-300">
                      <ShieldAlert className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-800 bg-white px-2 py-0.5 rounded-full border border-amber-200 shadow-xs">
                        Fraud Prevention • Already Checked In
                      </span>
                      <h3 className="text-lg font-black text-slate-900">
                        {scanResult.attendee?.fullName || 'Registered Attendee'}
                      </h3>
                      <p className="text-xs text-amber-800 font-medium">
                        Duplicate Badge Scan Detected
                      </p>
                    </div>
                    <div className="bg-white p-2.5 rounded-xl border border-amber-200 text-[11px] text-slate-700 text-left space-y-1 shadow-xs">
                      <p>
                        <span className="text-slate-500">First Scanned At:</span>{' '}
                        <span className="font-mono text-amber-800 font-bold">
                          {scanResult.originalCheckedInAt
                            ? new Date(scanResult.originalCheckedInAt).toLocaleTimeString()
                            : 'Earlier today'}
                        </span>
                      </p>
                      <p>
                        <span className="text-slate-500">Attendee:</span>{' '}
                        <span className="text-slate-900 font-semibold">
                          {scanResult.attendee?.fullName} ({scanResult.attendee?.companyName || 'Delegate'})
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {scanResult.status === 'payment_pending' && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-5 text-center space-y-2.5 shadow-xs">
                    <div className="w-12 h-12 bg-rose-100 text-rose-700 rounded-2xl flex items-center justify-center mx-auto border border-rose-300">
                      <AlertTriangle className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-rose-800 bg-white px-2.5 py-0.5 rounded-full border border-rose-300 shadow-xs">
                        PAYMENT PENDING — Direct Attendee to Registration Helpdesk
                      </span>
                      <h3 className="text-lg font-black text-slate-900">
                        {scanResult.attendee?.fullName || 'Unpaid Registration'}
                      </h3>
                      <p className="text-xs text-rose-800 font-bold">
                        {scanResult.message}
                      </p>
                    </div>
                    <div className="bg-white p-2.5 rounded-xl border border-amber-300 text-[11px] text-slate-700 text-left space-y-1 shadow-xs">
                      <p className="font-semibold text-amber-900">
                        Turnstile Locked: Delegate registration fee is unpaid or pending verification. Please direct visitor to the Pavilion 3 Registration Helpdesk counter.
                      </p>
                      {scanResult.attendee && (
                        <p>
                          <span className="text-slate-500">Attendee:</span>{' '}
                          <span className="text-slate-900 font-semibold">
                            {scanResult.attendee.fullName} ({scanResult.attendee.companyName || 'Delegate'})
                          </span>
                        </p>
                      )}
                      <p className="text-[10px] font-mono text-slate-500">Code: {scanResult.token}</p>
                    </div>
                  </div>
                )}

                {scanResult.status === 'invalid' && (
                  <div className="bg-rose-50 border border-rose-300 rounded-2xl p-5 text-center space-y-2.5 shadow-xs">
                    <div className="w-12 h-12 bg-rose-100 text-rose-700 rounded-2xl flex items-center justify-center mx-auto border border-rose-300">
                      <XCircle className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-rose-700 bg-white px-2 py-0.5 rounded-full border border-rose-200 shadow-xs">
                        Invalid Pass • Turnstile Locked
                      </span>
                      <h3 className="text-lg font-black text-slate-900">Badge Not Recognized</h3>
                      <p className="text-xs font-medium text-rose-800">{scanResult.message}</p>
                      <p className="text-xs text-rose-700 font-mono break-all">{scanResult.token}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 space-y-2 text-slate-400">
                <ScanLine className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="text-xs font-semibold text-slate-600">Awaiting Badge Scan</p>
                <p className="text-[10px] text-slate-500 max-w-xs mx-auto">
                  Hold QR code up to camera or enter token manually. Results update instantaneously.
                </p>
              </div>
            )}
          </div>

          {/* Recent Scans Stream */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-xs space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>Turnstile Activity Stream</span>
              </span>
              <span className="text-[10px] text-slate-500 font-mono">
                {scanHistory.length} Recorded
              </span>
            </div>

            {scanHistory.length === 0 ? (
              <p className="text-[11px] text-slate-500 text-center py-4">No scans recorded yet this session.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {scanHistory.map((item) => (
                  <div
                    key={item.id}
                    className="bg-[#F8F9F6] border border-slate-200 rounded-xl p-2.5 flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {item.status === 'granted' && (
                        <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0" />
                      )}
                      {(item.status === 'duplicate' || item.status === 'payment_pending') && (
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      )}
                      {item.status === 'invalid' && (
                        <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate text-[11px]">
                          {item.attendeeName || item.token}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {item.companyName || (item.status === 'granted' ? 'Checked in' : item.token)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 space-y-0.5">
                      <span className="text-[9px] font-mono text-slate-400 block">{item.timestamp}</span>
                      {item.tier && (
                        <span
                          className={`text-[8px] font-extrabold uppercase px-1.5 py-0.2 rounded border ${getTierBadge(
                            item.tier
                          )}`}
                        >
                          {item.tier.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
